import React, { useState, useContext, useRef, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import AuthContext from "../../contexts/AuthContext/AuthContext";
import LanguageInfoPanel from "../LanguageInfoPanel/LanguageInfoPanel";
import getVideoThumbnail from "../../utils/VideoThumbnail";
import "./TranslationOrder.css";
import "../../styles/colors.css";

const TranslationOrder = () => {
  const SERVER_URL = process.env.REACT_APP_SERVER_URL;
  const navigate = useNavigate();
  const { isSignedIn } = useContext(AuthContext);
  const fromToRef = useRef(null);
  const urlInputRef = useRef(null); // For focus management
  const [formData, setFormData] = useState({
    original_video_url: "",
    from_language: "",
    to_language: "",
  });
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [thumbnail, setThumbnail] = useState({
    url: null,
    error: null,
    loading: false,
  });

  useEffect(() => {
    if (!isSignedIn) {
      console.debug("User not signed in, redirecting to /signin");
      navigate("/signin");
    }
  }, [isSignedIn, navigate]);

  // Debounced thumbnail fetching
  useEffect(() => {
    let isMounted = true;
    const timer = setTimeout(async () => {
      if (!formData.original_video_url) {
        if (isMounted) {
          console.debug("TranslationOrder: Clearing thumbnail state");
          setThumbnail({ url: null, error: null, loading: false });
        }
        return;
      }

      if (isMounted) {
        console.debug(
          `TranslationOrder: Fetching thumbnail for ${formData.original_video_url}`
        );
        setThumbnail({ url: null, error: null, loading: true });
      }
      try {
        const { thumbnailUrl, error } = await getVideoThumbnail(
          formData.original_video_url
        );
        if (isMounted) {
          console.debug(
            `TranslationOrder: Thumbnail result - url: ${thumbnailUrl}, error: ${error}`
          );
          setThumbnail({ url: thumbnailUrl, error, loading: false });
        }
      } catch (e) {
        if (isMounted) {
          console.error("TranslationOrder: Thumbnail fetch failed", e);
          setThumbnail({
            url: null,
            error: "Failed to fetch thumbnail",
            loading: false,
          });
        }
      }
    }, 500); // Increased to 500ms for mobile network reliability

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [formData.original_video_url]);

  const handleLanguageSelectionChanged = ({
    id,
    name,
    number_of_speakers,
    location,
    code,
    flag,
    type,
  }) => {
    if (!type) {
      console.warn("TranslationOrder: Language selection missing type");
      return;
    }
    console.debug(`${type} language selected: ${name} (ID: ${id})`);
    if (type === "from") {
      setFormData((prev) => ({
        ...prev,
        from_language: id || "",
      }));
    } else if (type === "to") {
      setFormData((prev) => ({
        ...prev,
        to_language: id || "",
      }));
    }
    setError("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { original_video_url, from_language, to_language } = formData;

    if (!original_video_url || !from_language || !to_language) {
      setError(
        "Please fill out all required fields: Video URL, From Language, and To Language."
      );
      urlInputRef.current?.focus(); // Focus URL input on error
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const response = await axios.post(
        `${SERVER_URL}/api/initial-translation-orders/`,
        formData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${
              localStorage.getItem("access_token") || ""
            }`,
          },
        }
      );
      console.debug("Order created:", response.data);
      navigate("/initial-order-success");
    } catch (error) {
      console.error("Error submitting order:", error.response?.data);
      setError(
        error.response?.data?.message ||
          "Failed to submit order. Please try again."
      );
      urlInputRef.current?.focus();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isSignedIn) return null;

  return (
    <div className="translation-order-container">
      <Helmet>
        <title>Order Translation – LingoLab</title>
        <meta
          name="description"
          content="Order a new video translation with LingoLab. Select source and target languages, provide your video URL, and get started with AI-powered localization."
        />
        <meta name="robots" content="noindex, follow" />
        <link
          rel="canonical"
          href="https://lingolab.ca/initial-translation-order"
        />
        <meta property="og:title" content="Order Translation – LingoLab" />
        <meta
          property="og:description"
          content="Order a new video translation with LingoLab. Select source and target languages, provide your video URL, and get started with AI-powered localization."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://lingolab.ca/translation-order"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@LingLabAI" />
        <meta name="twitter:title" content="Order Translation – LingoLab" />
        <meta
          name="twitter:description"
          content="Order a new video translation with LingoLab. Select source and target languages, provide your video URL, and get started with AI-powered localization."
        />
        <meta name="twitter:image" content="https://lingolab.ca/ll-X.png" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "LingoLab",
              "url": "https://lingolab.ca/",
              "logo": "https://lingolab.ca/ll-X.png",
              "sameAs": [
                "https://twitter.com/LingLabAI"
              ],
              "description": "LingoLab uses advanced AI to translate and localize your videos into 80+ languages, preserving the original voice and syncing lips for a native look.",
              "contactPoint": {
                "@type": "ContactPoint",
                "email": "support@lingolab.ca",
                "contactType": "customer support",
                "url": "https://lingolab.ca/contact"
              }
            }
          `}
        </script>
      </Helmet>
      <motion.header
        className="translation-order-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }} // Reduced duration for mobile
      >
        <h1 className="translation-order-title">
          Order Your Video Translation
        </h1>
        <p className="translation-order-subtitle">
          Seamlessly translate your content with LingoLab
        </p>
        <div className="header-divider"></div>
      </motion.header>

      <motion.div
        className="translation-order-form-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }} // Reduced duration for mobile
      >
        <form onSubmit={handleSubmit} noValidate>
          {error && (
            <div className="error-message" role="alert" aria-live="assertive">
              {error}
            </div>
          )}
          <div className="form-section">
            <h2 className="form-section-title">Language Selection</h2>
            <LanguageInfoPanel
              ref={fromToRef}
              statsTextStorageKey="translationOrderStatsState"
              disableHide={false}
              translationStatsOnly={false}
              onLanguageSelectionChanged={handleLanguageSelectionChanged}
              fromStorageKey="orderFromVisibleLanguages"
              toStorageKey="orderToVisibleLanguages"
              selectionRequired={true}
            />
          </div>
          <div className="form-section">
            <label htmlFor="original_video_url" className="form-label">
              Video URL <span className="required">*</span>
            </label>
            <input
              ref={urlInputRef}
              type="url"
              id="original_video_url"
              name="original_video_url"
              value={formData.original_video_url}
              onChange={handleChange}
              required
              className="form-input"
              placeholder="https://example.com/video"
              aria-required="true"
              inputMode="url"
              aria-describedby={error ? "error-message" : undefined}
            />
            <div className="thumbnail-container mt-2 flex justify-center">
              {thumbnail.url ? (
                <motion.img
                  src={thumbnail.url}
                  alt="Video thumbnail"
                  className="thumbnail-image w-full max-w-[280px] rounded-lg shadow-sm"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  onError={() =>
                    setThumbnail({
                      url: "/placeholder.jpg",
                      error: null,
                      loading: false,
                    })
                  }
                />
              ) : thumbnail.error ? (
                <p className="text-red-500 text-sm text-center" role="alert">
                  {thumbnail.error}
                </p>
              ) : thumbnail.loading ? (
                <p
                  className="text-gray-600 text-sm text-center"
                  aria-live="polite"
                >
                  Loading thumbnail...
                </p>
              ) : null}
            </div>
          </div>
          <button
            type="submit"
            className="submit-button"
            disabled={isSubmitting}
            aria-busy={isSubmitting}
            aria-label={isSubmitting ? "Submitting order" : "Submit order"}
          >
            {isSubmitting ? "Submitting..." : "Submit Order"}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

export default TranslationOrder;
